import { useState, useEffect } from 'react';
import { 
  X, 
  ChevronLeft, 
  ChevronRight, 
  Download, 
  Share2, 
  MapPin, 
  Calendar, 
  Tag, 
  Camera,
  Copy,
  ExternalLink,
  Info
} from 'lucide-react';
import { Button } from '@/components/ui';
import { PhotoDetailModalProps } from '../types/photo';

export const PhotoDetailModal: React.FC<PhotoDetailModalProps> = ({
  photo,
  onClose,
  onPrevious,
  onNext
}) => {
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  // Reset image loading state when photo changes
  useEffect(() => {
    setImageLoading(true);
    setImageError(false);
  }, [photo.image_id]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          if (onPrevious) {
            event.preventDefault();
            onPrevious();
          }
          break;
        case 'ArrowRight':
          if (onNext) {
            event.preventDefault();
            onNext();
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, onPrevious, onNext]);

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return 'Invalid Date';
    }
  };

  const formatTime = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch {
      return 'Invalid Time';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Selected':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Under Review':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = photo.asset_url;
    link.download = `${photo.program}_${photo.image_id}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${photo.program} - ${photo.activity}`,
          text: `Photo from ${photo.program} program`,
          url: photo.asset_url,
        });
      } catch (err) {
        console.error('Error sharing:', err);
      }
    } else {
      copyToClipboard(photo.asset_url);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full">
          {/* Header */}
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <Tag className="h-6 w-6 text-gray-600" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    {photo.program} - {photo.activity}
                  </h3>
                  <p className="text-sm text-gray-500">
                    Image ID: {photo.image_id}
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                {/* Navigation buttons */}
                {onPrevious && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={onPrevious}
                    className="p-2"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                )}
                {onNext && (
                  <Button
                    variant="secondary" 
                    size="sm"
                    onClick={onNext}
                    className="p-2"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                )}

                {/* Close button */}
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={onClose}
                  className="p-2"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="px-4 pb-4 sm:px-6 sm:pb-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Image */}
              <div className="lg:col-span-2">
                <div className="relative bg-gray-100 rounded-lg overflow-hidden" style={{ aspectRatio: '4/3' }}>
                  {!imageError ? (
                    <>
                      {imageLoading && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Camera className="h-12 w-12 text-gray-400 animate-pulse" />
                        </div>
                      )}
                      <img
                        src={photo.asset_url}
                        alt={`${photo.program} - ${photo.activity}`}
                        className={`w-full h-full object-contain transition-opacity duration-200 ${
                          imageLoading ? 'opacity-0' : 'opacity-100'
                        }`}
                        onLoad={() => setImageLoading(false)}
                        onError={() => {
                          setImageError(true);
                          setImageLoading(false);
                        }}
                      />
                    </>
                  ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <Camera className="h-12 w-12 text-gray-400 mb-2" />
                      <p className="text-gray-500 text-sm">Failed to load image</p>
                    </div>
                  )}
                </div>

                {/* Action buttons */}
                <div className="mt-4 flex flex-wrap gap-3">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleDownload}
                    disabled={imageError}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </Button>
                  
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleShare}
                  >
                    <Share2 className="h-4 w-4 mr-2" />
                    Share
                  </Button>

                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => copyToClipboard(photo.asset_url)}
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    {copySuccess ? 'Copied!' : 'Copy URL'}
                  </Button>

                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => window.open(photo.asset_url, '_blank')}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Open Original
                  </Button>
                </div>
              </div>

              {/* Metadata */}
              <div className="space-y-6">
                {/* Status */}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center">
                    <Info className="h-4 w-4 mr-2" />
                    Status
                  </h4>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(photo.status)}`}>
                    {photo.status}
                  </span>
                </div>

                {/* Program Information */}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                    <Tag className="h-4 w-4 mr-2" />
                    Program Details
                  </h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Program:</span>
                      <span className="text-sm font-medium text-gray-900">{photo.program}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Activity:</span>
                      <span className="text-sm font-medium text-gray-900">{photo.activity}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Group ID:</span>
                      <span className="text-sm font-medium text-gray-900">{photo.image_group_id}</span>
                    </div>
                  </div>
                </div>

                {/* Location Information */}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                    <MapPin className="h-4 w-4 mr-2" />
                    Location Details
                  </h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">District:</span>
                      <span className="text-sm font-medium text-gray-900">
                        {(photo as any)?.village_context?.district_name || 'Not Available'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Sub District:</span>
                      <span className="text-sm font-medium text-gray-900">
                        {(photo as any)?.village_context?.subdistrict_name || 'Not Available'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Village:</span>
                      <span className="text-sm font-medium text-gray-900">
                        {(photo as any)?.village_context?.village_name || 'Not Available'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Submitted by:</span>
                      <span className="text-sm font-medium text-gray-500 italic">Change Maker (User info not available)</span>
                    </div>
                  </div>
                </div>

                {/* Date & Time */}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                    <Calendar className="h-4 w-4 mr-2" />
                    Date & Time
                  </h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Date:</span>
                      <span className="text-sm font-medium text-gray-900">{formatDate(photo.date)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Time:</span>
                      <span className="text-sm font-medium text-gray-900">{formatTime(photo.date)}</span>
                    </div>
                  </div>
                </div>

                {/* Technical Details */}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                    <Camera className="h-4 w-4 mr-2" />
                    Technical Details
                  </h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Image ID:</span>
                      <span className="text-sm font-mono text-gray-900 break-all">{photo.image_id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Format:</span>
                      <span className="text-sm font-medium text-gray-900">JPEG</span>
                    </div>
                  </div>
                </div>

                {/* Caption Generator (Future feature) */}
                <div className="border-t pt-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">
                    Auto-Generated Caption
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-700">
                      <strong>{photo.program}</strong> program activity "{photo.activity}" 
                      captured on {formatDate(photo.date)}
                      {(photo as any)?.village_context?.village_name && (
                        <> in {(photo as any).village_context.village_name}, {(photo as any).village_context.subdistrict_name}, {(photo as any).village_context.district_name}</>
                      )} by a Change Maker. This photo showcases the impact of Roundglass Foundation's work in the community.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};